"use client";

import { type KeyboardEvent } from "react";
import { PanelLeft, PanelRight } from "lucide-react";
import { classNames } from "../ui/class-names";
import type { WorkspaceTab } from "../editor/types";

export function MobileTabs({
  activeTab,
  onTabChange,
}: Readonly<{
  activeTab: WorkspaceTab;
  onTabChange: (tab: WorkspaceTab) => void;
}>) {
  function handleTabListKeyDown(event: KeyboardEvent<HTMLDivElement>) {
    if (event.key !== "ArrowLeft" && event.key !== "ArrowRight") {
      return;
    }

    event.preventDefault();

    const nextTab = activeTab === "editor" ? "preview" : "editor";
    const focusNextTab = () => {
      document.getElementById(`${nextTab}-tab`)?.focus();
    };

    onTabChange(nextTab);
    if (typeof window.requestAnimationFrame === "function") {
      window.requestAnimationFrame(focusNextTab);
    } else {
      focusNextTab();
    }
  }

  return (
    <div className="shrink-0 border-b border-zinc-200 bg-white px-3 py-2 md:hidden">
      <div
        role="tablist"
        aria-label="Workspace panes"
        onKeyDown={handleTabListKeyDown}
        className="grid grid-cols-2 rounded-md border border-zinc-200 bg-zinc-100 p-1"
      >
        <button
          id="editor-tab"
          type="button"
          role="tab"
          aria-selected={activeTab === "editor"}
          aria-controls="editor-pane"
          onClick={() => onTabChange("editor")}
          className={classNames(
            "inline-flex h-10 items-center justify-center gap-2 rounded px-3 text-sm font-medium focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand",
            activeTab === "editor"
              ? "bg-white text-zinc-950 shadow-sm"
              : "text-zinc-600",
          )}
        >
          <PanelLeft aria-hidden="true" className="size-4" />
          Editor
        </button>
        <button
          id="preview-tab"
          type="button"
          role="tab"
          aria-selected={activeTab === "preview"}
          aria-controls="preview-pane"
          onClick={() => onTabChange("preview")}
          className={classNames(
            "inline-flex h-10 items-center justify-center gap-2 rounded px-3 text-sm font-medium focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand",
            activeTab === "preview"
              ? "bg-white text-zinc-950 shadow-sm"
              : "text-zinc-600",
          )}
        >
          <PanelRight aria-hidden="true" className="size-4" />
          Preview
        </button>
      </div>
    </div>
  );
}
